import React, { useState, useEffect } from 'react';
import { useAuthStore } from '@/store/authStore';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Eye,
  Calendar,
  MapPin,
  Image as ImageIcon,
  X
} from 'lucide-react';

interface Event {
  id: string;
  title: string;
  description?: string;
  start_date: string;
  end_date?: string;
  location?: string;
  type: 'meeting' | 'celebration' | 'maintenance' | 'announcement' | 'other';
  status: 'upcoming' | 'ongoing' | 'completed' | 'cancelled';
  is_public: boolean;
  requires_rsvp: boolean;
  max_participants?: number;
  attachments?: any;
  created_at: string;
  updated_at?: string;
}

// Image Preview Component
const ImagePreview: React.FC<{ url: string; index: number; onRemove: () => void }> = ({ url, index, onRemove }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  // Log URL when component mounts
  React.useEffect(() => {
    console.log(`ImagePreview ${index} - URL:`, url);
  }, [url, index]);

  return (
    <div className="relative group">
      {imageLoading && !imageError && (
        <div className="w-full h-32 flex items-center justify-center bg-gray-100 border border-gray-200 rounded-lg">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}
      {!imageError ? (
        <img
          src={url}
          alt={`Event image ${index + 1}`}
          className={`w-full h-32 object-cover rounded-lg border border-gray-200 bg-gray-100 ${imageLoading ? 'hidden' : ''}`}
          onError={(e) => {
            console.error(`Image load error for URL (index ${index}):`, url);
            console.error('Error event:', e);
            setImageError(true);
            setImageLoading(false);
          }}
          onLoad={() => {
            console.log(`Image loaded successfully (index ${index}):`, url);
            setImageLoading(false);
          }}
        />
      ) : (
        <div className="w-full h-32 flex flex-col items-center justify-center bg-red-50 border border-red-200 rounded-lg text-red-600 text-xs p-2">
          <span>Failed to load</span>
          <span className="text-[10px] mt-1 break-all text-center">{url}</span>
        </div>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-opacity opacity-100 group-hover:opacity-100 z-10"
        title="Remove image"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

const EventsPage = () => {
  const { user } = useAuthStore();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    location: '',
    type: 'meeting' as const,
    status: 'upcoming' as const,
    is_public: true,
    requires_rsvp: false,
    max_participants: ''
  });
  const [images, setImages] = useState<string[]>([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/v1/events?limit=100', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setEvents(data.data || []);
      } else {
        setError('Failed to fetch events');
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      setError('Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingImages(true);
    setError(null);
    setSuccess(null);

    try {
      const { imageUploadService } = await import('@/services/imageUploadService');
      const fileArray = Array.from(files);
      
      const results = await imageUploadService.uploadMultipleImages(fileArray, 'general', true);
      
      console.log('Upload results:', results);
      
      const successfulUploads = results.filter(r => r.success);
      const failedUploads = results.filter(r => !r.success);

      if (successfulUploads.length > 0) {
        const newUrls = successfulUploads.map(r => {
          console.log('Image URL from response:', r.url);
          // Use URL directly from response - it's already a full URL
          // Only convert if it's not already a full URL
          if (r.url && (r.url.startsWith('http://') || r.url.startsWith('https://'))) {
            console.log('Using full URL directly:', r.url);
            return r.url;
          }
          // If it's a relative path, convert to public URL
          const convertedUrl = imageUploadService.getPublicUrl(r.url);
          console.log('Converted relative URL:', r.url, 'to:', convertedUrl);
          return convertedUrl;
        });
        const updatedImages = [...images, ...newUrls];
        console.log('Current images array:', images);
        console.log('New URLs to add:', newUrls);
        console.log('Updated images array:', updatedImages);
        setImages(updatedImages);
        setSuccess(`${successfulUploads.length} image(s) uploaded successfully`);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('No images were uploaded successfully. Please check the console for details.');
        setTimeout(() => setError(null), 5000);
      }

      if (failedUploads.length > 0) {
        setError(`Failed to upload ${failedUploads.length} image(s). ${failedUploads[0].message || 'Unknown error'}`);
        setTimeout(() => setError(null), 5000);
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      setError(error instanceof Error ? error.message : 'Failed to upload images');
      setTimeout(() => setError(null), 3000);
    } finally {
      setUploadingImages(false);
      // Reset file input to allow uploading the same file again
      e.target.value = '';
    }
  };

  const removeImage = (index: number) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      // Combine date and time for start_date
      let start_date = formData.start_date;
      if (formData.start_time) {
        start_date = `${formData.start_date}T${formData.start_time}:00`;
      }

      // Combine date and time for end_date
      let end_date = formData.end_date || null;
      if (end_date && formData.end_time) {
        end_date = `${end_date}T${formData.end_time}:00`;
      }

      const eventData = {
        title: formData.title,
        description: formData.description,
        start_date: start_date,
        end_date: end_date || undefined,
        location: formData.location,
        type: formData.type,
        status: formData.status,
        is_public: formData.is_public,
        requires_rsvp: formData.requires_rsvp,
        max_participants: formData.max_participants ? parseInt(formData.max_participants) : undefined,
        attachments: images.length > 0 ? { images } : undefined
      };

      const url = editingEvent 
        ? `/api/v1/events/${editingEvent.id}`
        : '/api/v1/events';
      
      const method = editingEvent ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(eventData)
      });

      if (response.ok) {
        setSuccess(editingEvent ? 'Event updated successfully' : 'Event created successfully');
        setShowModal(false);
        resetForm();
        fetchEvents();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to save event');
        setTimeout(() => setError(null), 3000);
      }
    } catch (error) {
      console.error('Error saving event:', error);
      setError('Failed to save event');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleEdit = async (event: Event) => {
    setEditingEvent(event);
    const startDate = new Date(event.start_date);
    const endDate = event.end_date ? new Date(event.end_date) : null;
    
    setFormData({
      title: event.title,
      description: event.description || '',
      start_date: startDate.toISOString().split('T')[0],
      end_date: endDate ? endDate.toISOString().split('T')[0] : '',
      start_time: startDate.toTimeString().split(' ')[0].slice(0, 5),
      end_time: endDate ? endDate.toTimeString().split(' ')[0].slice(0, 5) : '',
      location: event.location || '',
      type: event.type,
      status: event.status,
      is_public: event.is_public,
      requires_rsvp: event.requires_rsvp,
      max_participants: event.max_participants?.toString() || ''
    });

    // Load existing images from attachments and convert to public URLs
    if (event.attachments && event.attachments.images) {
      try {
        const { imageUploadService } = await import('@/services/imageUploadService');
        const imageArray = Array.isArray(event.attachments.images) ? event.attachments.images : [];
        // Convert all image URLs to public URLs
        const publicUrls = imageArray.map((url: string) => {
          if (!url) return '';
          return imageUploadService.getPublicUrl(url);
        });
        setImages(publicUrls.filter(url => url !== ''));
      } catch (error) {
        console.error('Error loading images:', error);
        setImages([]);
      }
    } else {
      setImages([]);
    }

    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this event?')) {
      return;
    }

    try {
      const response = await fetch(`/api/v1/events/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSuccess('Event deleted successfully');
        fetchEvents();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to delete event');
        setTimeout(() => setError(null), 3000);
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      setError('Failed to delete event');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleViewDetails = async (event: Event) => {
    // Convert image URLs to public URLs if needed
    const eventWithUrls = { ...event };
    if (eventWithUrls.attachments && eventWithUrls.attachments.images) {
      try {
        const { imageUploadService } = await import('@/services/imageUploadService');
        const imageArray = Array.isArray(eventWithUrls.attachments.images) ? eventWithUrls.attachments.images : [];
        const publicUrls = imageArray.map((url: string) => {
          if (!url) return '';
          // If already a full URL, use it directly
          if (url.startsWith('http://') || url.startsWith('https://')) {
            return url;
          }
          return imageUploadService.getPublicUrl(url);
        });
        eventWithUrls.attachments = {
          ...eventWithUrls.attachments,
          images: publicUrls.filter(url => url !== '')
        };
      } catch (error) {
        console.error('Error converting image URLs:', error);
      }
    }
    setSelectedEvent(eventWithUrls);
    setShowDetailModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      start_date: '',
      end_date: '',
      start_time: '',
      end_time: '',
      location: '',
      type: 'meeting',
      status: 'upcoming',
      is_public: true,
      requires_rsvp: false,
      max_participants: ''
    });
    setImages([]);
    setEditingEvent(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming': return 'bg-blue-100 text-blue-800';
      case 'ongoing': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'meeting': return 'bg-blue-100 text-blue-800';
      case 'celebration': return 'bg-green-100 text-green-800';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800';
      case 'announcement': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredEvents = events.filter(event => {
    const matchesSearch = event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (event.description && event.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesType = filterType === 'all' || event.type === filterType;
    const matchesStatus = filterStatus === 'all' || event.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading events...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {user?.role === 'member' ? 'Events' : 'Events Management'}
              </h1>
              <p className="mt-2 text-lg text-gray-600">
                {user?.role === 'member' 
                  ? 'View all society events' 
                  : 'Manage and view all society events'}
              </p>
            </div>
            {user?.role !== 'member' && (
              <button
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Create Event
              </button>
            )}
          </div>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded">
            {success}
          </div>
        )}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <input
                type="text"
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Types</option>
                <option value="meeting">Meeting</option>
                <option value="celebration">Celebration</option>
                <option value="maintenance">Maintenance</option>
                <option value="announcement">Announcement</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Status</option>
                <option value="upcoming">Upcoming</option>
                <option value="ongoing">Ongoing</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>

        {/* Events Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date & Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Public
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEvents.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                      No events found
                    </td>
                  </tr>
                ) : (
                  filteredEvents.map((event) => (
                    <tr key={event.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{event.title}</div>
                          {event.description && (
                            <div className="text-sm text-gray-500 truncate max-w-xs">
                              {event.description}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(event.type)}`}>
                          {event.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <div>
                            <div>{new Date(event.start_date).toLocaleDateString()}</div>
                            {event.end_date && (
                              <div className="text-xs text-gray-500">
                                to {new Date(event.end_date).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {event.location ? (
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            <span>{event.location}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(event.status)}`}>
                          {event.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${event.is_public ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {event.is_public ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleViewDetails(event)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Details"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                          {user?.role !== 'member' && (
                            <>
                              <button
                                onClick={() => handleEdit(event)}
                                className="text-indigo-600 hover:text-indigo-900"
                                title="Edit"
                              >
                                <Pencil className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleDelete(event.id)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Create/Edit Modal - Only show for non-members */}
        {showModal && user?.role !== 'member' && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingEvent ? 'Edit Event' : 'Create New Event'}
                </h3>
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                    <input
                      type="date"
                      required
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                    <input
                      type="time"
                      value={formData.start_time}
                      onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                    <input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                    <input
                      type="time"
                      value={formData.end_time}
                      onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="meeting">Meeting</option>
                      <option value="celebration">Celebration</option>
                      <option value="maintenance">Maintenance</option>
                      <option value="announcement">Announcement</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="upcoming">Upcoming</option>
                      <option value="ongoing">Ongoing</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Participants</label>
                  <input
                    type="number"
                    value={formData.max_participants}
                    onChange={(e) => setFormData({ ...formData, max_participants: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_public}
                      onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Public Event</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.requires_rsvp}
                      onChange={(e) => setFormData({ ...formData, requires_rsvp: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Requires RSVP</span>
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Event Images</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      disabled={uploadingImages}
                      className="hidden"
                      id="image-upload"
                    />
                    <label
                      htmlFor="image-upload"
                      className="flex flex-col items-center justify-center cursor-pointer"
                    >
                      <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-600">
                        {uploadingImages ? 'Uploading...' : 'Click to upload images'}
                      </span>
                    </label>
                  </div>
                  {images.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm text-gray-600 mb-2">
                        {images.length} image(s) uploaded
                      </p>
                      <div className="grid grid-cols-3 gap-4">
                        {images.map((url, index) => (
                          <ImagePreview
                            key={index}
                            url={url}
                            index={index}
                            onRemove={() => removeImage(index)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {images.length === 0 && !uploadingImages && (
                    <p className="mt-2 text-sm text-gray-500 text-center">
                      No images uploaded yet
                    </p>
                  )}
                </div>
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    {editingEvent ? 'Update' : 'Create'} Event
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Detail View Modal - Enhanced to match public page style */}
        {showDetailModal && selectedEvent && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 sm:top-20 mx-auto p-4 sm:p-6 border w-full max-w-4xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{selectedEvent.title}</h3>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                    <span className={`inline-flex px-3 py-1 text-xs sm:text-sm font-semibold rounded-full ${getTypeColor(selectedEvent.type)}`}>
                      {selectedEvent.type}
                    </span>
                    <span className={`inline-flex px-3 py-1 text-xs sm:text-sm font-semibold rounded-full ${getStatusColor(selectedEvent.status)}`}>
                      {selectedEvent.status}
                    </span>
                    <span className={`inline-flex px-3 py-1 text-xs sm:text-sm font-semibold rounded-full ${selectedEvent.is_public ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {selectedEvent.is_public ? 'Public' : 'Private'}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600 ml-4"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              {/* Event Images - Show prominently at top */}
              {selectedEvent.attachments && selectedEvent.attachments.images && selectedEvent.attachments.images.length > 0 && (
                <div className="mb-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {selectedEvent.attachments.images.map((url: string, index: number) => (
                      <div key={index} className="relative group">
                        <img
                          src={url}
                          alt={`Event image ${index + 1}`}
                          className="w-full h-48 sm:h-64 object-cover rounded-lg border border-gray-200"
                          onError={(e) => {
                            console.error('Image load error:', url);
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-4 sm:space-y-6">
                {selectedEvent.description && (
                  <div>
                    <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">Description</h4>
                    <p className="text-sm sm:text-base text-gray-600 whitespace-pre-wrap">{selectedEvent.description}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      Start Date & Time
                    </h4>
                    <p className="text-sm sm:text-base text-gray-900 font-medium">
                      {new Date(selectedEvent.start_date).toLocaleString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  {selectedEvent.end_date && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                        <Calendar className="w-4 h-4 mr-2" />
                        End Date & Time
                      </h4>
                      <p className="text-sm sm:text-base text-gray-900 font-medium">
                        {new Date(selectedEvent.end_date).toLocaleString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  )}
                </div>

                {selectedEvent.location && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <MapPin className="w-4 h-4 mr-2" />
                      Location
                    </h4>
                    <p className="text-sm sm:text-base text-gray-900 font-medium">{selectedEvent.location}</p>
                  </div>
                )}

                {selectedEvent.requires_rsvp && (
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h4 className="text-sm font-medium text-blue-900 mb-1">RSVP Required</h4>
                    <p className="text-sm text-blue-700">
                      {selectedEvent.max_participants ? `Maximum ${selectedEvent.max_participants} participants` : 'RSVP is required for this event'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsPage;
